import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { useBasket } from "@/context/BasketContext";
import { imageUrl, money } from "@/lib/api";
import { Button } from "@/components/ui/button";

const DELIVERY_FEE = 2.99;

export default function Basket() {
  const { items, updateQty, remove, subtotal, sellerCount } = useBasket();
  const navigate = useNavigate();
  const delivery = items.length ? DELIVERY_FEE * sellerCount : 0;
  const total = subtotal + delivery;

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center min-h-[60vh]">
        <div className="w-20 h-20 rounded-full bg-[#1A3C2A]/8 flex items-center justify-center mx-auto mb-4">
          <ShoppingBag size={34} className="text-[#1A3C2A]" />
        </div>
        <h1 className="font-display text-2xl font-bold text-[#1A3C2A]">Your basket is empty</h1>
        <p className="text-sm text-[#5A626A] mt-2">Discover something lovely from a local seller.</p>
        <Button data-testid="basket-empty-browse" onClick={() => navigate("/browse")} className="mt-5 rounded-full bg-[#1A3C2A] hover:bg-[#132E20]">Start shopping</Button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 min-h-[70vh]">
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1A3C2A] mb-6">Your basket</h1>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3">
          {items.map((it) => (
            <div key={it.product_id} data-testid={`basket-item-${it.product_id}`}
              className="bg-white border border-[#E5DEC9] rounded-2xl p-3 sm:p-4 flex gap-3 sm:gap-4 items-center">
              <img src={imageUrl(it.image)} alt={it.name} className="w-20 h-20 rounded-xl object-cover bg-[#F4EFE6] shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-semibold text-[#22252A] truncate">{it.name}</h3>
                <p className="text-xs text-[#5A626A]">{it.seller_name}</p>
                <p className="font-display font-bold text-[#1A3C2A] mt-1">{money(it.price)}</p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="flex items-center border border-[#E5DEC9] rounded-full">
                  <button data-testid={`basket-minus-${it.product_id}`} onClick={() => updateQty(it.product_id, it.quantity - 1)} className="p-2"><Minus size={14} /></button>
                  <span className="w-7 text-center text-sm font-semibold">{it.quantity}</span>
                  <button data-testid={`basket-plus-${it.product_id}`} onClick={() => updateQty(it.product_id, it.quantity + 1)} className="p-2"><Plus size={14} /></button>
                </div>
                <button data-testid={`basket-remove-${it.product_id}`} onClick={() => remove(it.product_id)} className="text-xs text-red-500 flex items-center gap-1 hover:underline"><Trash2 size={12} /> Remove</button>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white border border-[#E5DEC9] rounded-2xl p-5 h-fit sticky top-24">
          <h2 className="font-display font-bold text-[#1A3C2A] mb-4">Order summary</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-[#5A626A]">Subtotal</span><span data-testid="basket-subtotal" className="font-semibold">{money(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-[#5A626A]">Delivery ({sellerCount} seller{sellerCount > 1 ? "s" : ""})</span><span className="font-semibold">{money(delivery)}</span></div>
            <div className="h-px bg-[#E5DEC9] my-2" />
            <div className="flex justify-between text-base"><span className="font-bold text-[#1A3C2A]">Total</span><span data-testid="basket-total" className="font-display font-extrabold text-[#1A3C2A]">{money(total)}</span></div>
          </div>
          <Button data-testid="checkout-button" onClick={() => navigate("/checkout")} className="w-full mt-5 rounded-full bg-[#1A3C2A] hover:bg-[#132E20] h-12 font-semibold gap-2">
            Checkout <ArrowRight size={16} />
          </Button>
          <Button data-testid="continue-shopping" variant="outline" onClick={() => navigate("/browse")} className="w-full mt-2 rounded-full border-[#E5DEC9] h-11">Continue Shopping</Button>
          <p className="text-xs text-[#9A9282] text-center mt-3">Delivery fee is £2.99 per seller.</p>
        </div>
      </div>
    </div>
  );
}
